import type { GlobalConfig } from '@repo/go-ui';
import type { AxiosInstance } from 'axios';
import type { i18n } from 'i18next';
import { flowResult, makeAutoObservable, runInAction, set } from 'mobx';

import type { IDashboardService, Wellbore } from '../shared/api/IDashboardService';
import type { IWellDirectoryService, WellDto } from '../shared/api/IWellDirectoryService';
import type { IDirectoriesService } from '../shared/IDirectoriesService';
import type { INotificationsService } from '../shared/INotificationsService';
import { TimeZonesStore } from '../shared/stores/TimeZonesStore';
import type { IWidgetState, TUserData } from '../shared/types';
import { WidgetStatePublisher } from '../shared/WidgetStatePublisher';

import { openExitEditModeConfirmModal } from './openExitEditModeConfirmModal';
import { SettingsStore } from './stores/SettingsStore';
import { ViewStore } from './stores/ViewStore';
import type { AppParams, InitParameters, WidgetEvents, WidgetStateParams } from './types';

export class AlertsManagerWidgetState implements IWidgetState {
  appParams: AppParams;

  statePublisher: WidgetStatePublisher<WidgetStateParams>;

  status: 'initial' | 'loading' | 'ready' | 'failed' = 'initial';

  cause: string | null = null;

  settingsStore: SettingsStore;

  abortController: AbortController | null = null;

  timeZonesStore: TimeZonesStore;

  wellData: WellDto | null = null;

  wellDirectoryService: IWellDirectoryService;

  dashboardService: IDashboardService;

  viewStore: ViewStore | null = null;

  agent: AxiosInstance;

  directoriesService: IDirectoriesService;

  notifications: INotificationsService;

  userData: TUserData;

  getAccessToken: () => string | null;

  defaultDepthUnit: 'm' | 'ft';

  constructor(
    initParams: InitParameters,
    appParams: AppParams,
    initState: Partial<WidgetStateParams>,
    public eventHandlers: WidgetEvents,
  ) {
    this.wellDirectoryService = initParams.wellDirectoryApiService;
    this.dashboardService = initParams.dashboardApiService;
    this.agent = initParams.agent;
    this.directoriesService = initParams.directoriesService;
    this.notifications = initParams.notificationsService;
    this.defaultDepthUnit = initParams.defaultDepthUnit;
    this.userData = initParams.userData;
    this.getAccessToken = initParams.getAccessToken;

    this.appParams = appParams;
    this.statePublisher = new WidgetStatePublisher<WidgetStateParams>(
      initState,
      this.eventHandlers.onInternalStateChanged,
    );
    this.settingsStore = new SettingsStore(this.statePublisher);

    this.timeZonesStore = new TimeZonesStore(this.statePublisher.params.timeZone ?? 'user', (value) => {
      this.statePublisher.setParams({ timeZone: value });
    });

    makeAutoObservable(this, {
      agent: false,
      dashboardService: false,
      wellDirectoryService: false,
    });
    this.init();
  }

  setStatus(status: 'initial' | 'loading' | 'ready' | 'failed') {
    this.status = status;
  }

  setAppParams(params: Partial<AppParams>): void {
    if (params.wellId && params.wellId !== this.appParams.wellId) {
      this.setStatus('loading');
      this.setWell(params.wellId)
        .then(() => {
          this.setStatus('ready');
        })
        .catch(() => {
          this.setStatus('failed');
        });
    }
    set(this.appParams, params);
  }

  init() {
    this.setStatus('loading');

    this.setWell(this.appParams.wellId)
      .then(() => {
        if (this.eventHandlers.onInitializedSuccess) {
          this.eventHandlers.onInitializedSuccess();
          this.setStatus('ready');
        }
      })
      .catch((e) => {
        console.error(e);
        this.setStatus('failed');
      });
  }

  async setWell(wellId: number) {
    this.cause = null;
    await this.wellDirectoryService.getWell(wellId).then((well) => {
      runInAction(() => {
        this.wellData = well;
        this.timeZonesStore.setWellTimeZoneByString(well.timeZone);
      });
    });
    this.viewStore = new ViewStore(
      wellId,
      this.dashboardService,
      this.settingsStore,
      this.agent,
      this.notifications,
      this.directoriesService,
      this.wellDirectoryService,
      this.userData,
      this.getAccessToken,
      (wellbore: Wellbore) => {
        this.eventHandlers.onWellboreChanged?.(wellbore.specialName);
      },
    );
  }

  enterEditMode() {
    if (!this.viewStore) {
      return false;
    }
    this.viewStore.current.view.service.setEditing(true);
    if (this.eventHandlers.onEditModeChanged) {
      this.eventHandlers.onEditModeChanged(true);
    }
    return true;
  }

  async exitEditModeAsync(configProvider: GlobalConfig, i18n: i18n) {
    if (!this.viewStore) {
      return true;
    }

    const { current } = this.viewStore;

    const { service } = current.view;
    const hasUnsavedChanges = service.hasUnsavedChanges;
    if (hasUnsavedChanges) {
      const isConfirmed = await openExitEditModeConfirmModal(configProvider, i18n);
      if (!isConfirmed) {
        return false;
      }
      await flowResult(service.discardChanges());
    }

    service.setEditing(false);
    this.eventHandlers.onEditModeChanged?.(false);

    return true;
  }

  destroy(): void {}
}
